import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { Cover } from '../components/Cover';
import { IconBtn, TopBar } from '../components/ui';
import { useStore } from '../lib/store';
import { embed, topK, loadEmbedder, type EmbedProgress } from '../lib/embeddings';
import { getAllChunks, type LocalChunk } from '../lib/db';

type Filter = 'all' | 'books' | 'passages';

export function Search() {
  const nav = useNavigate();
  const books = useStore((s) => s.books);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [chunks, setChunks] = useState<LocalChunk[]>([]);
  const [results, setResults] = useState<{ book_id: string; chapter: number; page: number; text: string; score: number }[]>([]);
  const [embedderState, setEmbedderState] = useState<EmbedProgress | null>(null);
  const [busy, setBusy] = useState(false);
  const debouncer = useRef<number | null>(null);

  // Load chunks once
  useEffect(() => {
    getAllChunks().then(setChunks);
  }, []);

  // Pre-warm embedder so first query is fast
  useEffect(() => {
    loadEmbedder((p) => setEmbedderState(p)).catch(() => {});
  }, []);

  // Title/author filter (instant, no embedding needed)
  const bookHits = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return books.filter((b) => b.title.toLowerCase().includes(t) || (b.author || '').toLowerCase().includes(t));
  }, [q, books]);

  // Semantic search debounced
  useEffect(() => {
    if (debouncer.current) clearTimeout(debouncer.current);
    if (!q.trim() || q.trim().length < 3) { setResults([]); return; }
    debouncer.current = window.setTimeout(async () => {
      const usable = chunks.filter((c) => c.vec);
      if (!usable.length) return;
      setBusy(true);
      try {
        const [qv] = await embed([q.trim()]);
        const hits = topK(qv, usable.map((c) => ({ id: c.id, book_id: c.book_id, text: c.text, vec: c.vec! })), 8);
        const merged = hits.map((h) => {
          const src = chunks.find((c) => c.id === h.id)!;
          return { book_id: src.book_id, chapter: src.chapter, page: src.page, text: src.text, score: h.score };
        });
        setResults(merged);
      } catch (e) {
        console.warn('[search] failed', e);
      } finally {
        setBusy(false);
      }
    }, 250) as unknown as number;
  }, [q, chunks]);

  const ready = chunks.some((c) => c.vec);
  const indexedCount = chunks.filter((c) => c.vec).length;
  const totalCount = chunks.length;

  return (
    <div className="flex flex-col h-full safe-top">
      <TopBar left={<IconBtn name="back" onClick={() => nav(-1)} />} right={<IconBtn name="mic" />} title="" />
      <div className="px-5">
        <div className="flex items-center gap-2 h-12 px-4 rounded-2xl bg-paper dark:bg-night-800 shadow-soft">
          <Icon name="search" className="w-4 h-4 text-ink-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            className="flex-1 bg-transparent text-[14px] focus:outline-none"
            placeholder="Title, author, or a phrase..."
          />
          {q && (
            <button onClick={() => setQ('')} className="w-5 h-5 rounded-full bg-ink-300/40 flex items-center justify-center">
              <Icon name="close" className="w-3 h-3" />
            </button>
          )}
        </div>
        <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
          {(['all', 'books', 'passages'] as Filter[]).map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`h-8 px-3.5 rounded-full text-[12px] font-medium whitespace-nowrap capitalize ${filter === c ? 'bg-ink-900 text-cream-50 dark:bg-cream-100 dark:text-ink-900' : 'bg-black/5 dark:bg-white/10 text-ink-500'}`}
            >
              {c}
            </button>
          ))}
          {!ready && (
            <span className="ml-auto h-8 px-3 rounded-full bg-forest-500/10 text-forest-500 text-[11px] flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-coral animate-pulse" />
              Indexing {indexedCount}/{totalCount}
            </span>
          )}
        </div>
        {embedderState && embedderState.status === 'downloading' && embedderState.pct < 100 && (
          <div className="mt-2 text-[11px] text-ink-500">Loading semantic search · {embedderState.pct}%</div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar px-5 mt-4 pb-8">
        {!q && (
          <div className="text-center text-[12px] text-ink-500 mt-12 px-6">
            <Icon name="sparkle" className="w-5 h-5 mx-auto mb-2 text-forest-500" />
            <p className="font-medium text-ink-700 dark:text-ink-300 mb-1">Semantic + keyword search</p>
            <p>Type a title, author, or describe what you're looking for.</p>
            <p className="mt-2 opacity-70">e.g. "self 1 vs self 2", "loneliness in nature", "design simplicity"</p>
          </div>
        )}

        {/* Books matching */}
        {q && (filter === 'all' || filter === 'books') && bookHits.length > 0 && (
          <>
            <h3 className="text-[11px] uppercase tracking-[.2em] text-ink-500 mb-2">Books · {bookHits.length}</h3>
            <div className="space-y-2 mb-5">
              {bookHits.map((b) => (
                <button key={b.id} onClick={() => nav(`/book/${b.id}`)} className="w-full text-left p-3 rounded-2xl bg-paper dark:bg-night-800 shadow-soft flex gap-3">
                  <Cover kind={b.cover_kind} className="w-10 h-[60px] rounded-md shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">{b.title}</div>
                    <div className="text-[11px] text-ink-500 truncate">{b.author}</div>
                    <div className="text-[10px] text-ink-400 mt-0.5">{b.pages} pages · {Math.round(b.progress * 100)}% read</div>
                  </div>
                  <Icon name="chevRight" className="w-4 h-4 text-ink-400 self-center" />
                </button>
              ))}
            </div>
          </>
        )}

        {/* Semantic passages */}
        {q && (filter === 'all' || filter === 'passages') && (
          <>
            <h3 className="text-[11px] uppercase tracking-[.2em] text-ink-500 mb-2 flex items-center gap-2">
              <Icon name="sparkle" className="w-3 h-3 text-forest-500" />
              Passages · {results.length}
              {busy && <span className="text-coral">·</span>}
            </h3>
            {results.length === 0 && q.length >= 3 && !busy && ready && (
              <p className="text-[12px] text-ink-500">No passage matches. Try a different phrasing.</p>
            )}
            <div className="space-y-2">
              {results.map((r, i) => {
                const book = books.find((b) => b.id === r.book_id);
                if (!book) return null;
                return (
                  <button key={i} onClick={() => nav(`/read/${r.book_id}`)} className="w-full text-left p-3 rounded-2xl bg-paper dark:bg-night-800 shadow-soft flex gap-3">
                    <Cover kind={book.cover_kind} className="w-9 h-[54px] rounded-md shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <div className="text-[11px] text-forest-500 dark:text-forest-400 font-medium truncate">{book.title}</div>
                        <span className="text-[9px] text-ink-400 tabular-nums shrink-0 ml-2">{(r.score * 100).toFixed(0)}%</span>
                      </div>
                      <div className="text-[10px] text-ink-500">Ch {r.chapter} · p. {r.page}</div>
                      <p className="font-serif text-[13px] leading-snug mt-1.5 text-ink-700 dark:text-ink-300 line-clamp-3">"{r.text}"</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
